/**
 * `gstReturns.*` — read-only GSTR-3B preparation surface (V1.0).
 *
 * system-decisions §9: Flint prepares, CA files. The wire surface deliberately
 * has no `push to GSTN` or `mark filed` mutations in V1 — those land in V1.x
 * once the dashboard view of `prototype/compliance/gst-returns.html` ships.
 *
 * Single procedure: `getMonthSummary(period)` returns the GSTR-3B sub-line
 * aggregates the CA needs (Tables 3.1(a), 3.1(c), 3.1(d), 3.2, 4(A)(5), 5,
 * 6.1). Tables 3.1(b), 3.1(e), 4(B) are flagged as `unsupportedCodes` so
 * the SPA renders them muted with a "V1.x" tag.
 */
import { z } from "zod";
import { isoDate, moneyPaise } from "../_common.js";

/**
 * Signed paise — used on GSTR-3B summary rows where the value can be
 * negative (e.g. line 6.1 cgst when ITC carry-forward exceeds output).
 * Distinct from `moneyPaise` which is non-negative by convention.
 */
const signedPaise = z
  .string()
  .regex(/^-?\d+$/, "paise must be an integer string (can be negative on summary rows)");

// ── inputs ──────────────────────────────────────────────────────────

export const periodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "period must be YYYY-MM");

export const getMonthSummaryInput = z.object({
  period: periodSchema,
});

// ── outputs ─────────────────────────────────────────────────────────

const gstReturnLineCode = z.enum(["3.1(a)", "3.1(c)", "3.1(d)", "3.2", "4(A)(5)", "5", "6.1"]);

const gstReturnLine = z.object({
  code: gstReturnLineCode,
  label: z.string(),
  hint: z.string(),
  count: z.number().int().nonnegative(),
  // Line-level paise are SIGNED — the summary row (6.1) can go negative
  // when ITC > output GST for the period (the difference carries forward
  // to next month per GST law). Source-data rows stay non-negative; the
  // summary's negativity is constrained at the domain layer.
  taxablePaise: signedPaise,
  igstPaise: signedPaise,
  cgstPaise: signedPaise,
  sgstPaise: signedPaise,
  cessPaise: signedPaise,
  sourceEntityType: z.enum(["invoice", "bill"]).nullable(),
  sourceEntityIds: z.array(z.string().uuid()),
});

export const getMonthSummaryOutput = z.object({
  period: periodSchema,
  periodStart: isoDate,
  periodEnd: isoDate,
  dueOn: isoDate,
  company: z.object({
    legalName: z.string(),
    gstin: z.string().nullable(),
    state: z.string().nullable(),
    stateCode: z.string().nullable(),
  }),
  lines: z.array(gstReturnLine),
  totals: z.object({
    outputGstPaise: moneyPaise,
    inputItcPaise: moneyPaise,
    /** Can be negative when ITC exceeds output GST (carries forward to next period). */
    netCashPayablePaise: z.string().regex(/^-?\d+$/),
    invoiceCount: z.number().int().nonnegative(),
    billCount: z.number().int().nonnegative(),
  }),
  unsupportedCodes: z.array(z.string()).readonly(),
});

// ── derived TS types ──────────────────────────────────────────────
export type GetMonthSummaryInput = z.infer<typeof getMonthSummaryInput>;
export type GetMonthSummaryOutput = z.infer<typeof getMonthSummaryOutput>;
export type GstReturnLineCode = z.infer<typeof gstReturnLineCode>;
export type GstReturnLine = z.infer<typeof gstReturnLine>;
